/**
 * 맥스매치 Full 어드민 API — `/admin/max-match/full`.
 */
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import type { Pageable, PageResponse } from '@/entities/user/api/user.api';

/** 미니와 동일한 처방 단계. UNVERIFIABLE 은 전체 확인불가로 종료. */
export type MaxMatchFullStatus =
  | 'PENDING'
  | 'REQUEST'
  | 'USER_CONFIRMED'
  | 'ADMIN_CONFIRMED'
  | 'MATCHED'
  | 'UNVERIFIABLE';

/**
 * 이미지 확정 컬럼.
 * NO_ITEMS 는 사용자·OCR 단계의 품목 없음이고, UNVERIFIABLE 은 관리자 최종 판정이다.
 */
export type MaxMatchFullImageConfirmStatus =
  | 'UNCONFIRMED'
  | 'USER_CONFIRMED'
  | 'ADMIN_CONFIRMED'
  | 'NO_ITEMS'
  | 'UNVERIFIABLE';

export interface MaxMatchFullListItem {
  maxMatchFullPrescriptionId: number;
  prescriptionYearMonth: string;
  email: string;
  companyName?: string;
  clinicName: string;
  totalImageCount: number;
  confirmedImageCount: number;
  status: string;
  createdAt: string;
  latestRunId?: number | null;
  latestRunCreatedAt?: string | null;
}

export interface MaxMatchFullSearchParams {
  yearMonth: string;
  searchKeyword?: string;
  isRequest?: boolean;
}

export interface MaxMatchFullDetailImage {
  imageId: number;
  imageCode?: string;
  imageUrl?: string;
  confirmStatus?: string;
  adminConfirmedBy?: number | null;
  adminConfirmedAt?: string | null;
}

export interface MaxMatchFullDetailLine {
  sourceLineId?: number;
  imageId: number;
  status?: string;
  drugCode: string;
  hasDrugInfo?: boolean | null;
  pharmaCompanyName?: string;
  pharmaCompanyId?: number | null;
  drugName?: string;
  price?: number;
  outHospitalQuantity?: number;
  inHospitalQuantity?: number;
  totalAmount?: number;
  __tempRowKey?: string;
}

export interface MaxMatchFullDetail {
  maxMatchFullPrescriptionId: number;
  userId?: number;
  email: string;
  companyName?: string;
  clinicId?: number;
  clinicName?: string;
  sourceRefYm: string;
  status: string;
  createdAt: string;
  latestRunId?: number | null;
  latestRunCreatedAt?: string | null;
  images: MaxMatchFullDetailImage[];
  lines: MaxMatchFullDetailLine[];
}

export interface MaxMatchFullSourceLineRequest {
  imageId: number;
  drugCode: string;
  drugName?: string;
  pharmaCompanyId?: number | null;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
}

export interface MaxMatchFullSourceReplaceRequest {
  sourceRefYm: string;
  lines: MaxMatchFullSourceLineRequest[];
}

export interface MaxMatchFullRunResponse {
  runId: number;
  maxMatchFullPrescriptionId: number;
  sourceRefYm?: string;
  candidateRefYm?: string;
  status?: string;
  result?: Record<string, unknown>;
}

const asRecord = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

const asOptionalNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * 전체 확인불가 실행은 204 + 본문 없음.
 * 200 + 본문이 있으면 기존 실행 응답이다.
 */
export const parseMaxMatchFullRunResponse = (
  status: number,
  data: unknown,
  maxMatchFullPrescriptionId: number,
): MaxMatchFullRunResponse | null => {
  if (status === 204 || data == null || data === '') return null;
  const raw = asRecord(data);
  if (Object.keys(raw).length === 0) return null;
  return {
    runId: Number(raw.runId ?? raw.run_id ?? 0),
    maxMatchFullPrescriptionId: Number(
      raw.maxMatchFullPrescriptionId ??
        raw.max_match_full_prescription_id ??
        maxMatchFullPrescriptionId,
    ),
    sourceRefYm: String(raw.sourceRefYm ?? raw.source_ref_ym ?? ''),
    candidateRefYm: String(raw.candidateRefYm ?? raw.candidate_ref_ym ?? ''),
    status: String(raw.status ?? ''),
    result: asRecord(raw.result),
  };
};

/** 목록·상세 공통. 구 필드 sourceStatus 가 오면 status 로 흡수한다. */
const mapPrescriptionStatus = (row: Record<string, unknown>): string =>
  String(row.status ?? row.sourceStatus ?? row.source_status ?? '');

/**
 * 이미지 확정은 confirmStatus 한 컬럼.
 * 구 응답은 관리자 확정을 우선해 한 값으로 합친다.
 */
const mapImageConfirmStatus = (row: Record<string, unknown>): string => {
  const next = row.confirmStatus ?? row.confirm_status;
  if (next != null && String(next) !== '') return String(next);
  const admin = String(
    row.adminConfirmStatus ?? row.admin_confirm_status ?? '',
  );
  if (admin === 'ADMIN_CONFIRMED') return 'ADMIN_CONFIRMED';
  return String(row.userConfirmStatus ?? row.user_confirm_status ?? '');
};

export const mapMaxMatchFullListItem = (raw: unknown): MaxMatchFullListItem => {
  const row = asRecord(raw);
  return {
    maxMatchFullPrescriptionId: Number(
      row.maxMatchFullPrescriptionId ?? row.max_match_full_prescription_id ?? 0,
    ),
    prescriptionYearMonth: String(
      row.prescriptionYearMonth ?? row.prescription_year_month ?? '',
    ),
    email: String(row.email ?? ''),
    companyName: String(row.companyName ?? row.company_name ?? ''),
    clinicName: String(row.clinicName ?? row.clinic_name ?? ''),
    totalImageCount: Number(row.totalImageCount ?? row.total_image_count ?? 0),
    confirmedImageCount: Number(
      row.confirmedImageCount ?? row.confirmed_image_count ?? 0,
    ),
    status: mapPrescriptionStatus(row),
    createdAt: String(row.createdAt ?? row.created_at ?? ''),
    latestRunId: asOptionalNumber(row.latestRunId ?? row.latest_run_id),
    latestRunCreatedAt: String(
      row.latestRunCreatedAt ?? row.latest_run_created_at ?? '',
    ),
  };
};

export const mapMaxMatchFullDetailImage = (
  raw: unknown,
): MaxMatchFullDetailImage => {
  const row = asRecord(raw);
  return {
    imageId: Number(row.imageId ?? row.image_id ?? 0),
    imageCode: String(row.imageCode ?? row.image_code ?? ''),
    imageUrl: String(row.imageUrl ?? row.image_url ?? ''),
    confirmStatus: mapImageConfirmStatus(row),
    adminConfirmedBy: asOptionalNumber(
      row.adminConfirmedBy ?? row.admin_confirmed_by,
    ),
    adminConfirmedAt: String(
      row.adminConfirmedAt ?? row.admin_confirmed_at ?? '',
    ),
  };
};

export const mapMaxMatchFullDetailLine = (
  raw: unknown,
): MaxMatchFullDetailLine => {
  const row = asRecord(raw);
  return {
    sourceLineId:
      asOptionalNumber(row.sourceLineId ?? row.source_line_id) ?? undefined,
    imageId: Number(row.imageId ?? row.image_id ?? 0),
    status: String(row.status ?? ''),
    drugCode: String(row.drugCode ?? row.drug_code ?? ''),
    hasDrugInfo:
      row.hasDrugInfo == null && row.has_drug_info == null
        ? null
        : Boolean(row.hasDrugInfo ?? row.has_drug_info),
    pharmaCompanyName: String(
      row.pharmaCompanyName ?? row.pharma_company_name ?? '',
    ),
    pharmaCompanyId: asOptionalNumber(
      row.pharmaCompanyId ?? row.pharma_company_id,
    ),
    drugName: String(row.drugName ?? row.drug_name ?? ''),
    price: Number(row.price ?? 0),
    outHospitalQuantity: Number(
      row.outHospitalQuantity ?? row.out_hospital_quantity ?? 0,
    ),
    inHospitalQuantity: Number(
      row.inHospitalQuantity ?? row.in_hospital_quantity ?? 0,
    ),
    totalAmount: Number(row.totalAmount ?? row.total_amount ?? 0),
  };
};

export const mapMaxMatchFullDetail = (raw: unknown): MaxMatchFullDetail => {
  const row = asRecord(raw);
  const images = Array.isArray(row.images) ? row.images : [];
  const lines = Array.isArray(row.lines) ? row.lines : [];
  return {
    maxMatchFullPrescriptionId: Number(
      row.maxMatchFullPrescriptionId ?? row.max_match_full_prescription_id ?? 0,
    ),
    userId: asOptionalNumber(row.userId ?? row.user_id) ?? undefined,
    email: String(row.email ?? ''),
    companyName: String(row.companyName ?? row.company_name ?? ''),
    clinicId: asOptionalNumber(row.clinicId ?? row.clinic_id) ?? undefined,
    clinicName: String(row.clinicName ?? row.clinic_name ?? ''),
    sourceRefYm: String(row.sourceRefYm ?? row.source_ref_ym ?? ''),
    status: mapPrescriptionStatus(row),
    createdAt: String(row.createdAt ?? row.created_at ?? ''),
    latestRunId: asOptionalNumber(row.latestRunId ?? row.latest_run_id),
    latestRunCreatedAt: String(
      row.latestRunCreatedAt ?? row.latest_run_created_at ?? '',
    ),
    images: images
      .map(mapMaxMatchFullDetailImage)
      .sort((a, b) => a.imageId - b.imageId),
    lines: lines.map(mapMaxMatchFullDetailLine),
  };
};

export const buildFullSourceReplaceRequest = (
  sourceRefYm: string,
  lines: MaxMatchFullDetailLine[],
): MaxMatchFullSourceReplaceRequest => ({
  sourceRefYm,
  lines: lines
    .filter((line) => line.drugCode && line.imageId)
    .map((line) => ({
      imageId: line.imageId,
      drugCode: line.drugCode,
      drugName: line.drugName || undefined,
      pharmaCompanyId: line.pharmaCompanyId ?? undefined,
      outHospitalQuantity: Number(line.outHospitalQuantity ?? 0),
      inHospitalQuantity: Number(line.inHospitalQuantity ?? 0),
    })),
});

export const maxMatchFullService = {
  search: async (
    params: MaxMatchFullSearchParams,
    pageable: Pageable,
  ): Promise<PageResponse<MaxMatchFullListItem>> => {
    const response = await apiClient.get(createApiPath.maxMatch.fullSearch(), {
      params: {
        yearMonth: params.yearMonth,
        searchKeyword: params.searchKeyword || undefined,
        isRequest: params.isRequest,
        page: pageable.page,
        size: pageable.size,
      },
    });
    const page = response.data as PageResponse<unknown>;
    return {
      ...page,
      content: (page.content ?? []).map(mapMaxMatchFullListItem),
    } as PageResponse<MaxMatchFullListItem>;
  },

  getDetail: async (
    maxMatchFullPrescriptionId: number,
  ): Promise<MaxMatchFullDetail> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.fullDetail(maxMatchFullPrescriptionId),
    );
    return mapMaxMatchFullDetail(response.data);
  },

  replaceSource: async (
    maxMatchFullPrescriptionId: number,
    payload: MaxMatchFullSourceReplaceRequest,
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.maxMatch.fullSource(maxMatchFullPrescriptionId),
      payload,
    );
  },

  confirmImage: async (
    maxMatchFullPrescriptionId: number,
    imageId: number,
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.maxMatch.fullConfirmImage(
        maxMatchFullPrescriptionId,
        imageId,
      ),
    );
  },

  markImageUnverifiable: async (
    maxMatchFullPrescriptionId: number,
    imageId: number,
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.maxMatch.fullUnverifiableImage(
        maxMatchFullPrescriptionId,
        imageId,
      ),
    );
  },

  confirmBatch: async (
    maxMatchFullPrescriptionId: number,
    imageIds: number[],
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.maxMatch.fullConfirmBatch(maxMatchFullPrescriptionId),
      { imageIds },
    );
  },

  run: async (
    maxMatchFullPrescriptionId: number,
  ): Promise<MaxMatchFullRunResponse | null> => {
    const response = await apiClient.post(
      createApiPath.maxMatch.fullRuns(maxMatchFullPrescriptionId),
    );
    // 모든 이미지가 확인불가면 계산 없이 204. 오류가 아니라 종료다.
    return parseMaxMatchFullRunResponse(
      response.status,
      response.data,
      maxMatchFullPrescriptionId,
    );
  },

  getRun: async (runId: number): Promise<MaxMatchFullRunResponse> => {
    const response = await apiClient.get(createApiPath.maxMatch.fullRun(runId));
    const raw = asRecord(response.data);
    return {
      runId: Number(raw.runId ?? raw.run_id ?? runId),
      maxMatchFullPrescriptionId: Number(
        raw.maxMatchFullPrescriptionId ??
          raw.max_match_full_prescription_id ??
          0,
      ),
      sourceRefYm: String(raw.sourceRefYm ?? raw.source_ref_ym ?? ''),
      candidateRefYm: String(raw.candidateRefYm ?? raw.candidate_ref_ym ?? ''),
      status: String(raw.status ?? ''),
      result: asRecord(raw.result),
    };
  },

  getExcelDetails: async (
    runId: number,
  ): Promise<Record<string, unknown>[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.fullRunExcel(runId),
    );
    return Array.isArray(response.data) ? response.data : [];
  },
};
